'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function DudagaBotoksUygulamasiNedirNasilYapilirPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/dudaga-botoks-uygulamasi-nedir-nasil-yapilir';
  const pageTitle = 'Dudağa Botoks Uygulaması Nedir, Nasıl Yapılır?';
  const pageDescription = 'Dudağa botoks (lip flip) uygulaması nedir, nasıl yapılır? Dudak hacmini doğal biçimde belirginleştiren bu tekniğin farkı, süresi ve avantajları.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Dudağa Botoks Uygulaması', href: '/tr/blog/dudaga-botoks-uygulamasi-nedir-nasil-yapilir' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Dudağa botoks uygulaması ve lip flip tekniği değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-pink-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Dudak Estetiği
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Dudağa Botoks Uygulaması Nedir, Nasıl Yapılır?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>29 Ocak 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>9 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Dudağa botoks, son yıllarda doğal dudak belirginleştirme amacıyla uygulanan en popüler estetik yöntemlerden biridir. "Lip Flip" olarak da bilinen bu teknik, dolgu yapılmadan dudak konturunun yukarı doğru kıvrılmasını sağlar.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Amaç, hacim kazandırmadan dudak formunu yumuşatmak ve gülüş estetiğini dengelemektir. Dolguya göre daha hafif, daha doğal bir sonuç isteyen kişiler için ideal bir seçenektir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Dudağa Botoks (Lip Flip) Nedir?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dudağa botoks, üst dudağın kaslarını hafifçe gevşeterek dudağın dışa doğru dönmesini sağlayan bir işlemdir. Bu sayede dudak dolgun görünür, ancak dudak hacmi artırılmaz.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Uygulama sırasında botulinum toksini, dudak çevresindeki "orbicularis oris" kasına çok küçük dozlarda enjekte edilir. Amaç; kas aktivitesini kısmen azaltarak dudağın daha belirgin, kalkık ve dengeli görünmesini sağlamaktır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Dudağa Botoks ve Dudak Dolgusu Arasındaki Fark
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Özellik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Dudağa Botoks (Lip Flip)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Dudak Dolgusu
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Amaç</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Dudak hattını yumuşatmak, dudağı dışa çevirmek</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hacim kazandırmak</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">İçerik</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Botulinum toksin</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hyaluronik asit dolgu</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Etkisi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">2–3 gün içinde başlar, 3–4 ay sürer</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Anında görünür, 6–12 ay kalıcı</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Doğallık</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Son derece doğal, "şişme" olmaz</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Daha dolgun görünüm sağlar</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kullanım Alanı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Üst dudak ve gülüş hattı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Dudak içi hacim ve kontur</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-pink-50 rounded-xl p-6 mb-6">
                <p className="text-pink-800 font-medium">
                  💡 Kısacası: Dudağa botoks, dolguya göre daha hafif, doğal ve dinamik bir dudak şekli kazandırır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Uygulama Nasıl Yapılır?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                İşlem oldukça basittir:
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ol className="list-decimal list-inside text-blue-800 space-y-2">
                  <li>Bölgeye anestezik krem uygulanır</li>
                  <li>Çok ince uçlu iğnelerle üst dudak çevresine 4–6 küçük nokta halinde botoks enjekte edilir</li>
                  <li>Uygulama 10–15 dakika sürer, hemen sosyal yaşama dönülebilir</li>
                </ol>
                <div className="mt-4 space-y-2">
                  <p className="text-blue-800 font-medium">🕒 Etki 2–3 gün içinde başlar, 7. günde maksimum seviyeye ulaşır</p>
                  <p className="text-blue-800 font-medium">💎 Sonuç: Dudağın doğal kıvrımı belirginleşir, gülüş estetiği daha dengeli hale gelir</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Dudağa Botoksun Faydaları
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-green-900 mb-3">Faydalar:</h3>
                  <ul className="list-disc list-inside text-green-800 space-y-2">
                    <li>Dolgusuz doğal dudak belirginliği</li>
                    <li>Gülüş estetiğinde denge</li>
                    <li>Üst dudak çizgilerinin azalması</li>
                    <li>Simetri düzeltme</li>
                    <li>Hızlı, ağrısız ve güvenli uygulama</li>
                  </ul>
                </div>

                <div className="bg-pink-50 rounded-xl p-6">
                  <p className="text-pink-800 font-medium">
                    Bu özellikleriyle dudağa botoks, "az ama etkili" bir görünüm isteyen hastalar için ideal bir seçenektir.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Kimler İçin Uygundur?
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-green-900 mb-3">Uygun Adaylar:</h3>
                  <ul className="list-disc list-inside text-green-800 space-y-2">
                    <li>Doğal dudak belirginliği isteyenler</li>
                    <li>Gülerken dudağı tamamen kaybolan kişiler</li>
                    <li>Üst dudakta ince çizgiler bulunanlar</li>
                    <li>Dolgu yaptırmadan estetik görünüm isteyenler</li>
                  </ul>
                </div>

                <div className="bg-red-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-red-900 mb-3">⚠️ Kimlere önerilmez:</h3>
                  <ul className="list-disc list-inside text-red-800 space-y-2">
                    <li>Gebe veya emziren kişiler</li>
                    <li>Ağız çevresinde aktif enfeksiyonu olanlar</li>
                    <li>Nöromüsküler hastalığı bulunan bireyler</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Dudağa Botoks Sonrası Dikkat Edilmesi Gerekenler
              </h2>
              
              <div className="bg-yellow-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside text-yellow-800 space-y-2">
                  <li>İlk 24 saat dudak masajı veya öpüşme gibi hareketlerden kaçının</li>
                  <li>İlk 2 saat dik pozisyonda kalın</li>
                  <li>24 saat boyunca sıcak içecek, sauna ve spor yapılmamalıdır</li>
                  <li>Ruj, parlatıcı veya makyaj uygulaması ilk 12 saat önerilmez</li>
                </ul>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Dudağa botoks uygulaması ve doğal gülüş estetiği"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Dudağa botoks şişlik yapar mı?</h3>
                  <p className="text-gray-700">Hayır, dolgu gibi hacim vermez. Sadece dudağın yukarı kıvrılmasını sağlar.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Etkisi ne kadar sürer?</h3>
                  <p className="text-gray-700">Genellikle 3–4 ay kalıcıdır. Düzenli uygulamalarda etki süresi uzar.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Gülüşüm değişir mi?</h3>
                  <p className="text-gray-700">Evet, daha yumuşak ve estetik bir gülüş elde edilir; abartılı bir görünüm olmaz.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Dudağa botoks ile dolgu birlikte yapılabilir mi?</h3>
                  <p className="text-gray-700">Evet. Kombine edildiğinde hem şekil hem hacim açısından daha dengeli sonuçlar alınır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. İşlem sonrası dudak hissi kaybolur mu?</h3>
                  <p className="text-gray-700">Hayır, sadece kas gevşemesi olur; duyuda veya histe azalma olmaz.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Society of Plastic Surgeons (ASPS) – "Lip Flip vs. Lip Filler Techniques"</li>
                <li>Türk Estetik Tıp Derneği (TETD) – Botulinum Toxin Dudak Uygulamaları Rehberi</li>
                <li>Harvard Health Publishing – "Lip Botox: What You Should Know"</li>
                <li>Mayo Clinic – "Botulinum Toxin Safety and Facial Applications"</li>
                <li>ISAPS 2024 Global Report – "Minimally Invasive Aesthetic Trends"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#dudağabotoks</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#lipflip</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#doğalgülüş</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#ameliyatsızestetik</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#dudakestetiği</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-pink-600 to-purple-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Dudağa Botoks Uygulaması İçin Randevu Alın</h3>
              <p className="mb-6">Doğal dudak belirginleştirme ve gülüş estetiği için uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Dudağa botoks uygulaması hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-pink-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/botoks-ne-kadar-sure-gider"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">⏱️ Botoks Ne Kadar Süre Gider?</h4>
              <p className="text-gray-600 text-sm">Botoks etki süresi ve kalıcılık hakkında detaylı bilgiler.</p>
            </Link>
            <Link
              href="/tr/blog/botoks-fiyatlari-neden-farkli"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Fiyatları Neden Farklı?</h4>
              <p className="text-gray-600 text-sm">Estetik uygulamalarda fiyat farklılıklarının nedenleri.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
